// Appointments (PRD §4.14). Book, track, and send WhatsApp confirmations for
// customer appointments.
import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import Modal from '../components/ui/Modal';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import {
  useAppointments, saveAppointment, setAppointmentStatus, deleteAppointment, notifyAppointment,
  type Appointment, type ApptStatus,
} from '../lib/appointments';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const STATUS_PILL: Record<ApptStatus, string> = {
  booked: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-primary-50 text-primary',
  completed: 'bg-slate-100 text-ink-2',
  cancelled: 'bg-red-100 text-red-700',
};
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}
interface Draft { id?: string; customerName: string; phoneE164: string; service: string; startLocal: string; durationMin: number; notes: string; }
const emptyDraft = (): Draft => ({ customerName: '', phoneE164: '', service: '', startLocal: toLocalInput(Date.now() + 3600_000), durationMin: 30, notes: '' });

export default function Appointments() {
  const { t } = useI18n();
  const { appts, loading } = useAppointments();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function openEdit(a: Appointment) {
    setErr(null);
    setDraft({ id: a.id, customerName: a.customerName, phoneE164: a.phoneE164, service: a.service ?? '', startLocal: toLocalInput(a.startAt), durationMin: a.durationMin, notes: a.notes ?? '' });
  }

  async function save() {
    if (!draft || !draft.customerName.trim() || !draft.phoneE164.trim()) return;
    setBusy(true); setErr(null);
    try {
      await saveAppointment({
        id: draft.id, customerName: draft.customerName.trim(), phoneE164: draft.phoneE164.trim(),
        service: draft.service.trim() || undefined, startAt: new Date(draft.startLocal).getTime(),
        durationMin: draft.durationMin, notes: draft.notes.trim() || undefined,
      });
      setDraft(null);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function notify(a: Appointment) {
    const when = new Date(a.startAt).toLocaleString();
    const body = `Hi ${a.customerName}, this is a reminder of your appointment${a.service ? ` for ${a.service}` : ''} on ${when}.`;
    setNotice(null);
    try { await notifyAppointment(a.id, body); setNotice(t('appt.notified')); }
    catch (e) { setNotice(`${t('appt.notifyFail')}: ${(e as Error).message}`); }
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-extrabold text-ink">{t('appt.title')}</h1>
        <button type="button" onClick={() => { setErr(null); setDraft(emptyDraft()); }} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white sm:ms-auto">+ {t('appt.add')}</button>
      </div>
      <p className="mb-5 text-ink-3">{t('appt.subtitle')}</p>
      {notice && <div className="mb-4 rounded-[9px] bg-surface px-3 py-2 text-xs font-semibold text-ink-2">{notice}</div>}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
              <div className="space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-3 w-28" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-7 w-16 rounded-md" />
                <Skeleton className="h-7 w-16 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : appts.length === 0 ? (
        <div className="rounded-card border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-850">
          <EmptyState
            title={t('appt.empty')}
            description={t('appt.emptyDesc') || 'Schedule appointments and send automated WhatsApp reminders to your customers.'}
            action={
              <button
                type="button"
                onClick={() => { setErr(null); setDraft(emptyDraft()); }}
                className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white hover:bg-primary-dark transition"
              >
                + {t('appt.add')}
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {appts.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              <div className="min-w-[160px] flex-1">
                <div className="flex items-center gap-2">
                  <b className="text-sm text-ink">{a.customerName}</b>
                  <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_PILL[a.status])}>{t(`appt.status.${a.status}`)}</span>
                </div>
                <p className="text-xs text-ink-3">{new Date(a.startAt).toLocaleString()} · {a.durationMin}m{a.service ? ` · ${a.service}` : ''}</p>
                <p className="text-xs text-ink-3">{a.phoneE164}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {a.status !== 'confirmed' && <button type="button" onClick={() => void setAppointmentStatus(a.id, 'confirmed')} className="rounded-[8px] border border-primary px-2.5 py-1 text-xs font-bold text-primary">{t('appt.confirm')}</button>}
                {a.status !== 'completed' && <button type="button" onClick={() => void setAppointmentStatus(a.id, 'completed')} className="rounded-[8px] border border-slate-200 px-2.5 py-1 text-xs font-bold text-ink">{t('appt.complete')}</button>}
                <button type="button" onClick={() => void notify(a)} className="rounded-[8px] border border-slate-200 px-2.5 py-1 text-xs font-bold text-ink">{t('appt.remind')}</button>
                <button type="button" onClick={() => openEdit(a)} className="rounded-[8px] border border-slate-200 px-2.5 py-1 text-xs font-bold text-ink">{t('cat.edit')}</button>
                <button type="button" onClick={() => void deleteAppointment(a.id)} className="rounded-[8px] border border-red-500 px-2.5 py-1 text-xs font-bold text-red-500">{t('cat.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t('appt.editTitle') : t('appt.add')}
        footer={
          <>
            <button type="button" onClick={() => setDraft(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={save} disabled={busy || !draft?.customerName.trim() || !draft?.phoneE164.trim()} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('common.apply')}</button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <input value={draft.customerName} onChange={(e) => setDraft({ ...draft, customerName: e.target.value })} placeholder={t('appt.customer')} aria-label={t('appt.customer')} className={inputCls} autoFocus />
            <input value={draft.phoneE164} onChange={(e) => setDraft({ ...draft, phoneE164: e.target.value })} placeholder="+9687..." aria-label={t('subs.col.phone') || 'Phone'} className={inputCls} />
            <input value={draft.service} onChange={(e) => setDraft({ ...draft, service: e.target.value })} placeholder={t('appt.service')} aria-label={t('appt.service')} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-ink-3">{t('appt.when')}<input type="datetime-local" value={draft.startLocal} onChange={(e) => setDraft({ ...draft, startLocal: e.target.value })} className={inputCls} /></label>
              <label className="block text-xs text-ink-3">{t('appt.duration')}<input type="number" min={5} step={5} value={draft.durationMin} onChange={(e) => setDraft({ ...draft, durationMin: Number(e.target.value) })} className={inputCls} /></label>
            </div>
            <textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder={t('appt.notes')} aria-label={t('appt.notes')} className={inputCls} />
            {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
